import React, { useCallback, useEffect, useState } from 'react';
import { getCompensationConfig, saveCompensationConfig, getLocations } from '../api';
import AppHeader from './AppHeader';
import { RESERVED_OPTION_NAMES } from '../utils/submitForm';
import { isAdmin } from '../utils/roles';

function Compensation({ user, onLogout, onNavigate }) {
  const isAdminUser = isAdmin(user);
  const defaultLocation = user?.location || 'Six Flags Saint Louis';
  const [selectedLocation, setSelectedLocation] = useState(defaultLocation);
  const [locationOptions, setLocationOptions] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newOption, setNewOption] = useState('');

  useEffect(() => {
    if (!isAdminUser) return;
    getLocations()
      .then((data) => {
        const active = (data.locations || [])
          .filter((loc) => loc.active)
          .map((loc) => loc.name);
        setLocationOptions(active);
        if (active.length) {
          setSelectedLocation((current) =>
            active.includes(current) ? current : active[0]
          );
        }
      })
      .catch((err) => {
        setError(err.error || 'Unable to load locations');
      });
  }, [isAdminUser]);

  const normalizeItems = (rawItems) =>
    (rawItems || [])
      .map((item) => {
        let option = '';
        if (typeof item === 'string') {
          option = item.trim();
        } else if (typeof item?.option === 'string') {
          option = item.option.trim();
        } else if (typeof item?.option?.option === 'string') {
          option = item.option.option.trim();
        }
        return {
          option,
          active: item.active !== false && String(item.active).toLowerCase() !== 'false',
        };
      })
      .filter(
        (item) =>
          item.option &&
          item.option !== '[object Object]' &&
          !RESERVED_OPTION_NAMES.includes(item.option)
      );

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getCompensationConfig(selectedLocation);
      setItems(normalizeItems(data.items));
    } catch (err) {
      setError(err.error || 'Unable to load compensation options');
    } finally {
      setLoading(false);
    }
  }, [selectedLocation]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const persistItems = async (updatedItems, successMessage) => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await saveCompensationConfig(selectedLocation, updatedItems);
      await loadItems();
      if (successMessage) {
        setMessage(successMessage);
      }
    } catch (err) {
      setError(err.error || 'Unable to save compensation options');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = (option) => {
    const target = items.find((item) => item.option === option);
    if (!target) return;
    const updated = items.map((item) =>
      item.option === option ? { ...item, active: !item.active } : item
    );
    const label = target.active ? 'deactivated' : 'activated';
    persistItems(updated, `"${option}" ${label}.`);
  };

  const handleAddOption = async () => {
    const trimmed = newOption.trim();
    if (!trimmed) return;
    if (RESERVED_OPTION_NAMES.includes(trimmed)) {
      setError('Other is always available on submit forms and cannot be added here');
      return;
    }
    if (items.some((item) => item.option.toLowerCase() === trimmed.toLowerCase())) {
      setError('That option already exists for this location');
      return;
    }
    const updated = [...items, { option: trimmed, active: true }];
    setNewOption('');
    setShowAddForm(false);
    await persistItems(updated, `"${trimmed}" added.`);
  };


  return (
    <div className="container wide-container">
      <AppHeader
        user={user}
        pageTitle="Compensation Options"
        subtitle={
          isAdminUser
            ? 'Select a location to manage its resolution choices'
            : `Manage resolution choices for ${selectedLocation}`
        }
        onNavigate={onNavigate}
        onLogout={onLogout}
        active="compensation"
      />

      {error && <div className="error">{error}</div>}
      {message && <div className="success">{message}</div>}

      {isAdminUser && (
        <div className="form-group location-select-row">
          <label htmlFor="compensation-location">Location</label>
          <select
            id="compensation-location"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            disabled={saving || locationOptions.length === 0}
          >
            {locationOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
      )}

      <p className="subtitle" style={{ marginBottom: '20px' }}>
        All options for <strong>{selectedLocation}</strong> are stored in SQLite with an{' '}
        <strong>active</strong> flag. Inactive
        options are hidden from New Submission. <strong>Other</strong> is always available on
        every submit form (last in the list).
      </p>

      {loading ? (
        <div className="no-results">Loading compensation options…</div>
      ) : (
        <div className="compensation-panel">
          <div className="search-results">
            <table className="compensation-table">
              <thead>
                <tr>
                  <th>Compensation option</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="subtitle" style={{ textAlign: 'center' }}>
                      No options yet. Use + to add one.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.option} className={item.active ? '' : 'compensation-row-inactive'}>
                      <td>{item.option}</td>
                      <td>{item.active ? 'Yes' : 'No'}</td>
                      <td>
                        <button
                          type="button"
                          className={
                            item.active ? 'btn-table btn-deactivate' : 'btn-table btn-activate'
                          }
                          onClick={() => handleToggleActive(item.option)}
                          disabled={saving}
                        >
                          {item.active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="compensation-toolbar">
            {showAddForm ? (
              <div className="compensation-add-form">
                <input
                  type="text"
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  placeholder="New compensation option"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddOption();
                    }
                    if (e.key === 'Escape') {
                      setShowAddForm(false);
                      setNewOption('');
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleAddOption}
                  disabled={saving}
                >
                  Add
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewOption('');
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="compensation-add-btn"
                onClick={() => setShowAddForm(true)}
                disabled={saving}
                aria-label="Add compensation option"
                title="Add option"
              >
                +
              </button>
            )}
          </div>

          <p className="subtitle" style={{ marginTop: '16px' }}>
            <strong>Other</strong> is not listed above — it is always shown last on the New
            Submission resolution dropdown at every location.
          </p>
        </div>
      )}
    </div>
  );
}

export default Compensation;
